import {
    type FC,
    type PropsWithChildren,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
} from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Search_RequestBody } from "@opensearch-project/opensearch/api";
import type { TotalHits } from "@opensearch-project/opensearch/api/_types/_core.search";

import { mapHitSourceToOpenSearchProductRow } from "../mappers/map-opensearch-product-hit";
import { ProductSearchService } from "../services/product-search.service";
import type { OpenSearchStore } from "../types/products";
import { buildProductSearchRootQuery } from "../utils/build-search-query-filter";
import { mapProductSearchHits } from "../utils/map-product-search-hits";
import {
    OPENSEARCH_PRODUCTS_CATALOG_PAGE_SIZE,
    OPENSEARCH_PRODUCTS_STALE_MS,
    PRODUCT_NAME_SEARCH_FIELDS,
} from "../utils/product-search-constants";

import { OpenSearchProductsContext } from "./OpenSearchProductsContext";

const DEFAULT_PAGE_SIZE = OPENSEARCH_PRODUCTS_CATALOG_PAGE_SIZE;
const STALE_TIME_MS = OPENSEARCH_PRODUCTS_STALE_MS;

const DEFAULT_NAME_SEARCH_FIELDS = PRODUCT_NAME_SEARCH_FIELDS;

type OpenSearchProductsProviderProps = PropsWithChildren<{
    store: OpenSearchStore | null;
    /** Reserved for index filters aligned with storefront SearchService. */
    customerType?: string;
    /** Reserved for medical-only catalog flags on the index. */
    isStrictMedicalEnabled?: boolean;
    initialPage?: number;
    initialPageSize?: number;
    initialSearchName?: string;
    nameSearchFields?: string[];
}>;

const OpenSearchProductsProvider: FC<OpenSearchProductsProviderProps> = ({
    children,
    store,
    customerType,
    isStrictMedicalEnabled,
    initialPage = 0,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialSearchName = "",
    nameSearchFields = DEFAULT_NAME_SEARCH_FIELDS,
}) => {
    const [searchName, setSearchName] = useState(initialSearchName);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [brandFilter, setBrandFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
    const [page, setPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [brandOptions, setBrandOptions] = useState<string[]>([]);

    const deferredSearchName = useDeferredValue(searchName.trim());

    const catalogFilters = useMemo(
        () => ({
            category: categoryFilter,
            brand: brandFilter,
            isActive: statusFilter,
        }),
        [categoryFilter, brandFilter, statusFilter],
    );

    useEffect(() => {
        setPage(0);
    }, [deferredSearchName, categoryFilter, brandFilter, statusFilter]);

    const clearCatalogFilters = useCallback(() => {
        setCategoryFilter(null);
        setBrandFilter(null);
        setStatusFilter(null);
        setSearchName("");
    }, []);

    const searchService = useMemo(() => {
        if (
            !store?.mso_store_entity_id ||
            !store?.mso_org_id ||
            !store.short_name
        ) {
            return null;
        }
        return new ProductSearchService(
            store.mso_store_entity_id,
            store.mso_org_id,
            { storeShortName: store.short_name },
        );
    }, [store]);

    const searchBody: Search_RequestBody = useMemo(
        () => ({
            from: page * pageSize,
            size: pageSize,
            query: buildProductSearchRootQuery(
                deferredSearchName,
                nameSearchFields,
                catalogFilters,
            ),
        }),
        [page, pageSize, deferredSearchName, nameSearchFields, catalogFilters],
    );

    const bodyKey = JSON.stringify(searchBody);

    const enabled = Boolean(
        searchService &&
            store?.mso_store_entity_id &&
            store?.mso_org_id &&
            store.short_name,
    );

    const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
        queryKey: [
            "opensearch-products",
            store?.short_name ?? "",
            store?.mso_store_entity_id ?? "",
            store?.mso_org_id ?? "",
            customerType ?? "",
            Boolean(isStrictMedicalEnabled),
            deferredSearchName,
            categoryFilter ?? "",
            brandFilter ?? "",
            statusFilter === null ? "" : String(statusFilter),
            page,
            pageSize,
            bodyKey,
        ],
        queryFn: () => searchService!.search({ searchBody }),
        enabled,
        placeholderData: keepPreviousData,
        staleTime: STALE_TIME_MS,
    });

    const products = useMemo(() => {
        return mapProductSearchHits(
            data?.hits?.hits,
            mapHitSourceToOpenSearchProductRow,
        );
    }, [data]);

    const totalHits = (data?.hits?.total as TotalHits | undefined)?.value ?? 0;

    // Grow facet menus as pages load (aggs broke this index mapping).
    useEffect(() => {
        if (!products.length) {
            return;
        }

        setCategoryOptions(prev => {
            const next = new Set(prev);
            products.forEach(row => {
                const value = (row.category || "").trim();
                if (value) next.add(value);
            });
            return Array.from(next).sort((a, b) => a.localeCompare(b));
        });

        setBrandOptions(prev => {
            const next = new Set(prev);
            products.forEach(row => {
                const value = (row.brand || "").trim();
                if (value) next.add(value);
            });
            return Array.from(next).sort((a, b) => a.localeCompare(b));
        });
    }, [products]);

    const refetchProducts = () => {
        void refetch();
    };

    return (
        <OpenSearchProductsContext.Provider
            value={{
                products,
                totalHits,
                searchName,
                setSearchName,
                categoryFilter,
                setCategoryFilter,
                brandFilter,
                setBrandFilter,
                statusFilter,
                setStatusFilter,
                categoryOptions,
                brandOptions,
                clearCatalogFilters,
                page,
                setPage,
                pageSize,
                setPageSize,
                isLoading,
                isFetching,
                isError,
                error: error ?? null,
                refetch: refetchProducts,
            }}
        >
            {children}
        </OpenSearchProductsContext.Provider>
    );
};

export { OpenSearchProductsProvider };
